import { z } from 'zod';

import {
  ZodB2bContract,
  ZodB2bContractStatus,
  ZodB2bContractType,
} from '@Common/types/bo/b2b/contracts';
import { ZodB2bContractCustodyFees } from '@Common/types/bo/b2b/contracts/fees';
import { buildZodBoListQueryParsed } from '@Common/types/bo/utils/build_zod_bo_list_query';
import { ZodIDPathParam } from '@Common/types/request';
import { createZodArrayable } from '@Common/types/zod/zod-arrayable';
import { endpoint, GetEndpointType } from '@Common/types/zod/zod-endpoint';

export const ZodBoExternalContract = ZodB2bContract.omit({
  signature_date: true,
});

export const ZodGetBoExternalContractsResponse = z.array(
  ZodBoExternalContract.omit({ dist_correspondent_email_state: true }),
);
export type GetBoExternalContractsResponse = z.TypeOf<typeof ZodGetBoExternalContractsResponse>;

const standard = ZodB2bContractCustodyFees.options[0].pick({
  custody_config: true,
  paid_by_for_current_employees: true,
  paid_by_for_past_employees: true,
  paid_by_for_retired_employees: true,
});

const customAll = ZodB2bContractCustodyFees.options[1].pick({
  custody_config: true,
  paid_by_for_current_employees: true,
  paid_by_for_past_employees: true,
  paid_by_for_retired_employees: true,
});

const customPer = ZodB2bContractCustodyFees.options[2].pick({
  custody_config: true,
  paid_by_for_current_employees_pee: true,
  paid_by_for_past_employees_pee: true,
  paid_by_for_retired_employees_pee: true,
  paid_by_for_current_employees_pereco: true,
  paid_by_for_past_employees_pereco: true,
  paid_by_for_retired_employees_pereco: true,
});

const customFlatRate = ZodB2bContractCustodyFees.options[3].pick({
  custody_config: true,
  paid_by_for_current_employees: true,
  paid_by_for_past_employees: true,
  paid_by_for_retired_employees: true,
});

const customOldPricing = ZodB2bContractCustodyFees.options[4].pick({
  custody_config: true,
  paid_by_for_current_employees: true,
  paid_by_for_past_employees: true,
  paid_by_for_retired_employees: true,
});

const ZodGetBoExternalContractsOneResponse = z.object({
  ...ZodBoExternalContract.shape,
  fees: z
    .object({
      custody: z.discriminatedUnion('custody_config', [
        standard,
        customAll,
        customPer,
        customFlatRate,
        customOldPricing,
      ]),
    })
    .nullable(),
});
export type GetBoExternalContractsOneResponse = z.TypeOf<
  typeof ZodGetBoExternalContractsOneResponse
>;

const ZodBoExternalContractsFilters = z
  .object({
    ...ZodBoExternalContract.pick({
      contract_number: true,
      relationship_type: true,
      version: true,
      is_active: true,
    }).shape,
    id: createZodArrayable(z.number()),
    b2b_company_id: createZodArrayable(z.number()),
    contract_type: createZodArrayable(ZodB2bContractType),
    is_liv: createZodArrayable(z.boolean()),
    not_status: createZodArrayable(ZodB2bContractStatus),
  })
  .partial();

export type BoExternalContractsFilters = z.TypeOf<typeof ZodBoExternalContractsFilters>;

export const getBoExternalContractsSortColumns = [
  'id',
  'created_at',
  'version',
  'is_active',
  'effective_date',
] as const;

export const ZodBoExternalContractsQuery = buildZodBoListQueryParsed(
  ZodBoExternalContractsFilters,
  getBoExternalContractsSortColumns,
  {
    range: [0, 100],
  },
);
export type BoExternalContractsQuery = z.TypeOf<typeof ZodBoExternalContractsQuery>;

export const getBoExternalContractsEndpoint = endpoint({
  method: 'GET',
  queryParams: ZodBoExternalContractsQuery,
  responseBody: ZodGetBoExternalContractsResponse,
});
export type GetBoExternalContractsEndpoint = GetEndpointType<typeof getBoExternalContractsEndpoint>;

export const getBoExternalContractsIdEndpoint = endpoint({
  method: 'GET',
  pathParams: ZodIDPathParam,
  responseBody: ZodGetBoExternalContractsOneResponse,
});
export type GetBoExternalContractsIdEndpoint = GetEndpointType<
  typeof getBoExternalContractsIdEndpoint
>;
